<template>
	<Modal width="650px" height="70vh" :showModal="open" @close-modal="closeModal()" :backDrop="true">
		<template v-slot:title>
			<h2>{{ t('devlogs.title') }}</h2>
		</template>
		<template v-slot:content>
			<div v-for="(update, index) in updates" :key="index" style="padding: 0 .5rem;">
				<div class="flex justify-content-between align-center">
					<div class="flex gap-4 align-center">
						<div :class="['icon', { firstIcon: index === 0 }]" :style="{ color: get_color(update.type) }">
							<img :style="{ height: get_icon_size(update.type) }" :src="get_icon(update.type)"
								alt="Icon" />
						</div>
						<h3 style="font-size: 1.5rem;">Version {{ update.version }}</h3>
					</div>
					<p style="color: var(--gris-200); font-size: 0.8rem; font-style: italic;">{{ formatDate(update.date) }}</p>
				</div>

				<div class="content-group" :class="{ firstContent: index === 0 }"
					:style="{ '--bar-color': get_color(update.type) }">
					<div v-for="(group, groupKey) in getGroupedContent(update)" :key="groupKey">
						<div v-if="group.length > 0">
							<h4 class="subtitle">{{ t('devlogs.' + groupKey) }}</h4>
							<ul>
								<li v-for="(item, idx) in group" :key="idx">
									{{ item.message }}
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</template>
	</Modal>
</template>

<script setup>
import { defineProps } from 'vue';
const emit = defineEmits('updateModal');

import Modal from '@/components/utilities/modal/modal.vue';
import glass_icon from '@/assets/icon/glass-type.svg';
import shield_icon from '@/assets/icon/shield-type.svg';
import star_icon from '@/assets/icon/star-type.svg';

// pinia
import { storeToRefs } from 'pinia';
import { usedevLogsStore } from '@/store/global_settings/devLogs';
const dev_logs = usedevLogsStore();
const { updates } = storeToRefs(dev_logs);

import { useI18n } from 'vue-i18n';
const { t, locale } = useI18n({});

const props = defineProps({
	open: Boolean,
});

const closeModal = () => {
	emit('updateModal');
};

const getGroupedContent = (update) => {
	const grouped = {
		change: [],
		feature: [],
		bug: []
	};

	if (update.content && update.content[locale.value]) {
		update.content[locale.value].forEach(contentItem => {
			const issueName = contentItem.issue.name || contentItem.issue;
			switch (issueName) {
				case 'change':
					grouped.change.push(contentItem);
					break;
				case 'feature':
					grouped.feature.push(contentItem);
					break;
				case 'bug':
					grouped.bug.push(contentItem);
					break;
			}
		});
	}
	return grouped;
};

const get_icon = (type) => {
	switch (type.name || type) {
		case 'minor':
			return shield_icon;
		case 'medium':
			return star_icon;
		case 'major':
			return glass_icon;
		default:
			return shield_icon;
	}
};

const get_icon_size = (type) => {
	switch (type.name || type) {
		case 'minor':
			return '2rem';
		case 'medium':
			return '1.7rem';
		case 'major':
			return '1.9rem';
		default:
			return '1.7rem';
	}
};

const get_color = (type) => {
	switch (type.name || type) {
		case 'minor':
			return ' var(--bleu-500)';
		case 'medium':
			return ' var(--jaune-500)';
		case 'major':
			return ' var(--jaune-500)';
		default:
			return ' var(--bleu-500)';
	}
};

const formatDate = (dateString) => {
	if (!dateString) return '';
	const parts = dateString.split('-');
	if (parts.length === 3) {
		return `${parts[2]}-${parts[1]}-${parts[0]}`;
	}
	return dateString;
};
</script>

<style lang="scss" scoped>
.icon {
	position: relative;
	width: 3.5rem;
	height: 3.5rem;
	display: inline-block;
	border-radius: 50%;
	overflow: hidden;

	img {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		display: block;
	}

	&::after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		border-radius: 50%;
		border: 4px solid currentColor;
		box-sizing: border-box;
		display: block;
	}

	&.firstIcon::after {
		display: block;
	}

	&:not(.firstIcon)::after {
		display: none;
	}
}

.content-group {
	position: relative;
	padding: 0.5rem 0 1rem 5rem;

	h4 {
		margin: 1rem 0 0 0;
	}

	&::before {
		content: '';
		position: absolute;
		top: 7.5%;
		left: 1.6rem;
		width: 4px;
		height: 85%;
		background-color: var(--gris-200);
		border-radius: 5rem;
	}

	ul {
		list-style: disc;
		padding-left: 1rem;
		margin-top: 0.2rem;
		color: var(--gris-800);
	}

	&.firstContent::before {
		background-color: var(--bar-color);
	}
}
</style>
